import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, X, Database, Shield, Smartphone, Heart, Pill, Activity } from 'lucide-react';

interface UserTutorialProps {
  onComplete: () => void;
  isVisible: boolean;
}

const tutorialSteps = [
  {
    icon: Database,
    title: "Your Data Stays Private",
    content: "All your health information is stored locally on your device. No servers, no accounts, completely private.",
    action: "No signup required!"
  },
  {
    icon: Heart,
    title: "Track Health Conditions",
    content: "Add and monitor your medical conditions with ICD-10 codes. Track severity, status, and treatment progress.",
    action: "Try adding a condition"
  },
  {
    icon: Pill,
    title: "Manage Medications",
    content: "Keep track of your medications, dosages, and adherence. Set reminders and monitor interactions.",
    action: "Add your medications"
  },
  {
    icon: Activity,
    title: "Log Symptoms & Vitals",
    content: "Record symptoms, track their severity over time, and monitor vital signs and health metrics.",
    action: "Start tracking symptoms"
  },
  {
    icon: Shield,
    title: "Data Import & Export",
    content: "Export your data as JSON or CSV anytime. Import existing health data to get started quickly.",
    action: "Your data, your control"
  },
  {
    icon: Smartphone,
    title: "Works Everywhere",
    content: "Access your health data on any device. The app works offline and syncs when you're back online.",
    action: "Ready to start?"
  }
];

export const UserTutorial: React.FC<UserTutorialProps> = ({ onComplete, isVisible }) => {
  const [currentStep, setCurrentStep] = useState(0);

  if (!isVisible) return null;

  const nextStep = () => {
    if (currentStep < tutorialSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const skipTutorial = () => {
    onComplete();
  };

  const step = tutorialSteps[currentStep];
  const Icon = step.icon;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-slate-800">ZippyHealth Quick Tour</h2>
            <button 
              onClick={skipTutorial}
              className="text-slate-400 hover:text-slate-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-red-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon className="h-8 w-8 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-slate-800 mb-2">{step.title}</h3>
            <p className="text-slate-600 mb-4">{step.content}</p>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-red-800 font-medium text-sm">{step.action}</p>
            </div>
          </div>

          <div className="flex items-center justify-between mb-4">
            <button 
              onClick={prevStep}
              disabled={currentStep === 0}
              className="flex items-center space-x-1 text-slate-500 hover:text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-4 w-4" />
              <span>Back</span>
            </button>

            <div className="flex space-x-2">
              {tutorialSteps.map((_, index) => (
                <div 
                  key={index}
                  className={`w-2 h-2 rounded-full ${
                    index === currentStep ? 'bg-red-600' : 'bg-slate-300'
                  }`}
                />
              ))}
            </div>

            <button 
              onClick={nextStep}
              className="flex items-center space-x-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg"
            >
              <span>{currentStep === tutorialSteps.length - 1 ? 'Get Started' : 'Next'}</span>
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          <div className="text-center">
            <button 
              onClick={skipTutorial}
              className="text-sm text-slate-500 hover:text-slate-700"
            >
              Skip tour
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}; 
